// src/pages/PurchaseRecord/components/PurchaseRecordFilters.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Card, Row, Col, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../PurchaseRecordSlice";
import { fetchPurchaseRecord } from "../PurchaseRecordThunks";
import { useDefaultDateRange } from "../../../../../hooks/useDefaultDateRange"
import SearchListAutoComplete from "../../../../../components/SearchListAutoComplete";
import RecordDateFilter from "../../../../../components/filters/RecordDateFilter";
import {
  fetchEmployees,
  fetchSuppliers,
  fetchItems,
  fetchGroups,
  fetchCategories,
  fetchUsers,
  fetchTagGroups,
  fetchTagGroupFields,
  fetchLocations,
} from "../../../../../store/commonThunks/lookups";
import CommonDateRangeFilter from "../../../../../components/common/filters/CommonDateRangeFilter";

const PurchaseRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.purchaseRecord);
  const lookups = useSelector((state) => state.lookups);
  const [searchParams] = useSearchParams();

  useDefaultDateRange(setFilters, filters, "thisMonth");

  // Drill-down with a full range (e.g. from the Trial Balance): apply it and
  // the auto-fetch effect below runs the report once the dates are in place.
  useEffect(() => {
    const dateFrom = searchParams.get("dateFrom");
    const dateTo = searchParams.get("dateTo");
    if (dateFrom && dateTo) {
      dispatch(
        setFilters({
          filterType: "All",
          recordType: "without item",
          fromDate: dateFrom,
          toDate: dateTo,
        })
      );
    }
  }, []);

  // preload lookups
  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchSuppliers());
    dispatch(fetchItems());
    dispatch(fetchGroups());
    dispatch(fetchCategories());
    dispatch(fetchUsers());
    dispatch(fetchTagGroups());
    dispatch(fetchLocations());
  }, [dispatch]);

  // auto-fetch when filters are ready
  useEffect(() => {
    if (filters.filterType && filters.recordType && filters.fromDate && filters.toDate) {
      handleReport();
    }
  }, [filters]);

  const handleFilterChange = (field, value) => {
    if (field === "filterType") {
      const autoWithoutItem = ["By Item", "By Tag", "By Group", "By Category"];
      dispatch(
        setFilters({
          filterType: value,
          recordType: autoWithoutItem.includes(value) ? "without item" : filters.recordType,
          supplierId: null,
          itemId: null,
          groupId: null,
          categoryId: null,
          userId: null,
          locationId: null,
          tag_groupnameid: null,
          tagFeildNameid: null,
          voucherNo: null,
          invoiceNo: null,
        })
      );
      dispatch(setFilters({})); // ensure state update
      return;
    }

    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    const payload = {
      selectedFilterType: filters.filterType,
      selectedRecordType: filters.recordType,
      filters: {
        ...filters,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      },
    };

    dispatch(fetchPurchaseRecord(payload)).then((res) => {
      if (res.meta?.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };

  const renderExtraField = () => {
    switch (filters.filterType) {
      case "By Employee":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Employee</label>
            <SearchListAutoComplete
              entityType="employee"
              optionsList={(lookups.employees || []).map((e) => ({ sale_id: e.employee_id, display_text: e.employee_name }))}
              onSelect={(obj) => handleFilterChange("employeeId", obj?.sale_id)}
              onClear={() => handleFilterChange("employeeId", null)}
              placeholder="Search Employee"
              showAddNew={false}
              allowClear
            />
          </Col>
        );
      case "By Supplier":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Supplier</label>
            <SearchListAutoComplete
              entityType="supplier"
              optionsList={(lookups.suppliers || []).map((e) => ({ sale_id: e.acc_id, display_text: e.acc_name }))}
              onSelect={(obj) => handleFilterChange("supplierId", obj?.sale_id)}
              onClear={() => handleFilterChange("supplierId", null)}
              placeholder="Supplier"
              showAddNew={false}
              allowClear
            />
          </Col>
        );
      case "By Item":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Item</label>
            <SearchListAutoComplete
              entityType="item"
              optionsList={(lookups.items || []).map((e) => ({ sale_id: e.item_id, display_text: e.item_name }))}
              onSelect={(obj) => handleFilterChange("itemId", obj?.sale_id)}
              onClear={() => handleFilterChange("itemId", null)}
              placeholder="Item"
              showAddNew={false}
              allowClear
            />
          </Col>
        );
      case "By Group":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Group</label>
            <SearchListAutoComplete
              entityType="group"
              optionsList={(lookups.groups || []).map((e) => ({ sale_id: e.group_id, display_text: e.group_name }))}
              onSelect={(obj) => handleFilterChange("groupId", obj?.sale_id)}
              onClear={() => handleFilterChange("groupId", null)}
              placeholder="Group"
              showAddNew={false}
              allowClear
            />
          </Col>
        );
      case "By Category":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Category</label>
            <SearchListAutoComplete
              entityType="category"
              optionsList={(lookups.categories || []).map((e) => ({ sale_id: e.category_id, display_text: e.category_name }))}
              onSelect={(obj) => handleFilterChange("categoryId", obj?.sale_id)}
              onClear={() => handleFilterChange("categoryId", null)}
              placeholder="Category"
              showAddNew={false}
              allowClear
            />
          </Col>
        );
      case "By User":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search User</label>
            <SearchListAutoComplete
              entityType="user"
              optionsList={(lookups.users || []).map((u) => ({ sale_id: u.id, display_text: u.full_name }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              onClear={() => handleFilterChange("userId", null)}
              placeholder="User"
              showAddNew={false}
              allowClear
            />
          </Col>
        );
      case "By Tag":
        return (
          <>
            <Col xs={24} sm={12} md={8} xl={5}>
              <label>Tag Group</label>
              <SearchListAutoComplete
                optionsList={(lookups.tagGroups || []).map((tg) => ({ sale_id: tg.id, display_text: tg.tag_groupname }))}
                onSelect={(obj) => {
                  handleFilterChange("tag_groupnameid", obj?.sale_id);
                  if (obj?.sale_id) dispatch(fetchTagGroupFields(obj.sale_id));
                }}
                onClear={() => {
                  handleFilterChange("tag_groupnameid", null);
                  handleFilterChange("tagFeildNameid", null);
                }}
                placeholder="Tag Group"
                showAddNew={false}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} xl={5}>
              <label>Tag Field</label>
              <SearchListAutoComplete
                optionsList={(lookups.tagFields || []).map((tf) => ({ sale_id: tf.id, display_text: tf.display_text || tf.combinedField }))}
                onSelect={(obj) => handleFilterChange("tagFeildNameid", obj?.sale_id)}
                onClear={() => handleFilterChange("tagFeildNameid", null)}
                placeholder="Tag Field"
                showAddNew={false}
                allowClear
              />
            </Col>
          </>
        );
      case "By Location":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Location</label>
            <SearchListAutoComplete
              key={filters?.filterType}
              optionsList={(lookups.locations || []).map((l) => ({ sale_id: l.location_id, display_text: l.location_name }))}
              onSelect={(obj) => handleFilterChange("locationId", obj?.sale_id)}
              onClear={() => handleFilterChange("locationId", null)}
              placeholder="Location"
              showAddNew={false}
              allowClear
            />
          </Col>
        );
      case "By Voucher":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Voucher No</label>
            <SearchListAutoComplete
              optionsList={[]} // empty, use free text or implement search if needed
              onSelect={(obj) => handleFilterChange("voucherNo", obj?.sale_id ?? obj)}
              onClear={() => handleFilterChange("voucherNo", null)}
              placeholder="Voucher No"
              allowClear
              showAddNew={false}
            />
          </Col>
        );
      case "By Invoice":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Invoice No</label>
            <SearchListAutoComplete
              optionsList={[]}
              onSelect={(obj) => handleFilterChange("invoiceNo", obj?.sale_id ?? obj)}
              onClear={() => handleFilterChange("invoiceNo", null)}
              placeholder="Invoice No"
              allowClear
              showAddNew={false}
            />
          </Col>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="product-replace-filters mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} wrap style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By Supplier", display_text: "By Supplier" },
              { sale_id: "By Item", display_text: "By Item" },
              { sale_id: "By Tag", display_text: "By Tag" },
              { sale_id: "By Group", display_text: "By Group" },
              { sale_id: "By Category", display_text: "By Category" },
              { sale_id: "By Item Serial", display_text: "By Item Serial" },
              { sale_id: "By Location", display_text: "By Location" },
              { sale_id: "By User", display_text: "By User" },
              { sale_id: "By Voucher", display_text: "By Voucher" },
              { sale_id: "By Invoice", display_text: "By Invoice" },
            ]}
            value={{ value: filters.filterType, label: filters.filterType }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {renderExtraField()}

        {/* Record Type */}
        {!["By Item", "By Tag", "By Group", "By Category"].includes(filters.filterType) && (
          <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
            <label>Record Type</label>
            <SearchListAutoComplete
              optionsList={[
                { sale_id: "with item", display_text: "With Item" },
                { sale_id: "without item", display_text: "Without Item" },
              ]}
              value={
              filters.recordType
                ? { value: filters.recordType, label: filters.recordType === "with item"? "With Item" : "Without Item"}
                : undefined
            }
              onSelect={(obj) => handleFilterChange("recordType", obj?.sale_id)}
              placeholder="Record Type"
              showAddNew={false}
            />
          </Col>
        )}
        
        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />  

        <Col xs={24} sm={12} md={9} xl={3} style={{ display: "flex", alignItems: "flex-end", height: 58 }}>
          <Button data-testid="report-btn" type="primary" icon={<SearchOutlined />} onClick={handleReport} block>
            Report
          </Button>
        </Col>
      </Row>
      <style>{`
    @media (min-width: 768px) and (max-width: 1024px) {
      .product-replace-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
  `}</style>
    </Card>
  );
};

export default PurchaseRecordFilters;
